import React from "react";

import { X } from "lucide-react";

import { cn } from "~/lib/utils";

// AIDEV-NOTE: Unified banner component for top-fixed banners (surveys, cookies, notifications)
// Supports color variants with proper contrast for all interactive elements
//
// Usage examples:
// - Survey CTA: variant="forest", with custom children for Link/button
// - Cookie consent: variant="cream", with primaryAction/secondaryAction
// - Notification: variant="forest", with dismissible + onDismiss
//
// Color variants:
// - "forest": Green background (brand-forest-600), white text, white buttons
// - "cream": Beige background (brand-cream-100), dark text, dark buttons

export interface BannerProps {
    /** Main heading text */
    title: string;
    /** Optional description/body text */
    description?: string | React.ReactNode;
    /** Color variant affecting background and text colors */
    variant?: "forest" | "cream";
    /** Primary action button */
    primaryAction?: {
        label: string;
        onClick: () => void;
    };
    /** Secondary action button */
    secondaryAction?: {
        label: string;
        onClick: () => void;
    };
    /** Show dismiss (X) button */
    dismissible?: boolean;
    /** Dismiss button callback */
    onDismiss?: () => void;
    /** Additional content to render (e.g., custom buttons, links) */
    children?: React.ReactNode;
    /** Additional classes for the banner container */
    className?: string;
    /**
     * "fixed" (default) pins it to the top of the viewport — surveys, cookie consent.
     * "inline" renders it in the page flow at its parent's width, so the CTA sits next
     * to the copy instead of at the far edge of a 7xl row.
     */
    position?: "fixed" | "inline";
    /** Test ID for Playwright tests */
    "data-testid"?: string;
}

export function Banner({
    title,
    description,
    variant = "forest",
    primaryAction,
    secondaryAction,
    dismissible = false,
    onDismiss,
    children,
    className,
    position = "fixed",
    "data-testid": dataTestId,
}: BannerProps) {
    const isInline = position === "inline";
    // Variant-specific styles
    const variantStyles = {
        forest: {
            container: "bg-brand-forest-600 border-brand-forest-700",
            title: "text-white",
            description: "text-brand-cream-100",
            primaryButton:
                "bg-white text-brand-forest-700 hover:bg-brand-cream-50 shadow-sm",
            secondaryButton:
                "bg-transparent text-white border border-white/30 hover:bg-brand-forest-700",
            dismissButton: "text-white hover:bg-brand-forest-700",
        },
        cream: {
            container: "bg-brand-cream-100 border-brand-cream-300",
            title: "text-brand-forest-800",
            description: "text-brand-forest-700",
            primaryButton:
                "bg-brand-forest-700 text-white hover:bg-brand-forest-800 shadow-sm",
            secondaryButton:
                "bg-transparent text-brand-forest-700 border border-brand-forest-300 hover:bg-brand-cream-200",
            dismissButton: "text-brand-forest-600 hover:bg-brand-cream-200",
        },
    };

    const styles = variantStyles[variant];

    return (
        <div
            data-testid={dataTestId}
            className={cn(
                "animate-in fade-in duration-300 print:hidden",
                isInline
                    ? "my-4"
                    : "fixed left-0 right-0 top-0 z-50 slide-in-from-top-4",
                className,
            )}
        >
            <div
                className={cn(
                    "px-4 py-3 sm:px-6",
                    isInline ? "rounded-lg border" : "border-b shadow-lg",
                    styles.container,
                )}
            >
                <div
                    className={cn(
                        "flex gap-4",
                        isInline
                            ? "flex-col sm:flex-row sm:items-center sm:justify-between"
                            : "mx-auto max-w-7xl items-center justify-between",
                    )}
                >
                    <div className="flex-1 min-w-0">
                        <h4 className={cn("font-semibold", styles.title)}>
                            {title}
                        </h4>
                        {description && (
                            <div
                                className={cn(
                                    "mt-0.5 text-sm",
                                    styles.description,
                                )}
                            >
                                {description}
                            </div>
                        )}
                    </div>
                    <div
                        className={cn(
                            "flex flex-shrink-0 items-center gap-3",
                            isInline && "self-end sm:self-auto",
                        )}
                    >
                        {children}
                        {secondaryAction && (
                            <button
                                type="button"
                                onClick={secondaryAction.onClick}
                                className={cn(
                                    "whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors",
                                    styles.secondaryButton,
                                )}
                            >
                                {secondaryAction.label}
                            </button>
                        )}
                        {primaryAction && (
                            <button
                                type="button"
                                onClick={primaryAction.onClick}
                                className={cn(
                                    "whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium transition-colors",
                                    styles.primaryButton,
                                )}
                            >
                                {primaryAction.label}
                            </button>
                        )}
                        {dismissible && onDismiss && (
                            <button
                                onClick={onDismiss}
                                className={cn(
                                    "rounded-md p-1.5 transition-colors",
                                    styles.dismissButton,
                                )}
                                aria-label="Dismiss"
                            >
                                <X className="size-5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
